import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Autocomplete,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Warning,
  Star,
  StarBorder,
  Business,
  AdminPanelSettings,
  PersonRemove,
  PersonAdd,
  CameraAlt,
  HourglassTop,
  ExitToApp,
  NotificationsActive,
  NotificationsOff,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { updateUserDocument, addOrganizationToUser, setDefaultOrganization, leaveOrganization, getCoAdminsByOrganization, getVolunteersByOrganization, promoteToCoAdmin, removeCoAdmin } from '@services/userService';
import { updateUserProfile, updateUserPassword } from '@services/authService';
import { verifyOrganizationByInviteCode } from '@services/organizationService';
import { enablePushNotifications, disablePushNotifications } from '@services/pushNotificationService';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@services/firebase';
import { VOLUNTEER_SKILLS } from '@utils/constants';
import EmailVerificationBanner from '@components/common/EmailVerificationBanner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUserData, userOrganizations, isOwner } = useAuth();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Join org state
  const [joinInviteCode, setJoinInviteCode] = useState('');
  const [joiningOrg, setJoiningOrg] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');
  const [settingDefault, setSettingDefault] = useState(false);

  // Leave org state
  const [leaveOrgTarget, setLeaveOrgTarget] = useState(null);
  const [leavingOrg, setLeavingOrg] = useState(false);

  // Push notification state
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState('');

  // Profile form state
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [skills, setSkills] = useState([]);

  // Co-admin state (owner only)
  const [coAdmins, setCoAdmins] = useState([]);
  const [orgVolunteers, setOrgVolunteers] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotingAdmin, setPromotingAdmin] = useState(false);
  const [removingAdmin, setRemovingAdmin] = useState(null);
  const [coAdminError, setCoAdminError] = useState('');
  const [coAdminSuccess, setCoAdminSuccess] = useState('');

  // Photo upload state
  const fileInputRef = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Password change state
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (userData) {
      setDisplayName(userData.displayName || '');
      setPhoneNumber(userData.phoneNumber || '');
      setSmsOptIn(userData.smsOptIn || false);
      setSkills(userData.skills || []);
    }
  }, [userData]);

  useEffect(() => {
    if (userData?.organizationId && isOwner()) {
      getCoAdminsByOrganization(userData.organizationId).then(setCoAdmins).catch(console.error);
      getVolunteersByOrganization(userData.organizationId).then(setOrgVolunteers).catch(console.error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.organizationId, userData?.role]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Update Firebase Auth profile
      await updateUserProfile({
        displayName: displayName,
      });

      // Update Firestore user document
      const updates = {
        displayName: displayName,
        phoneNumber: phoneNumber,
        phone: phoneNumber, // Also store as 'phone' for Cloud Functions compatibility
        smsOptIn: smsOptIn,
      };

      // Only update skills for volunteers
      if (userData?.userType === 'volunteer') {
        updates.skills = skills;
      }

      await updateUserDocument(currentUser.uid, updates);

      // Refresh user data
      await refreshUserData();

      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setError('');
      setSuccess('');

      // Validation
      if (!newPassword || !confirmPassword) {
        setError('Please fill in all password fields.');
        return;
      }

      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('New passwords do not match.');
        return;
      }

      setLoading(true);

      // Update password
      await updateUserPassword(newPassword);

      setSuccess('Password changed successfully!');
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password. You may need to sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setChangingPassword(false);
    // Reset form to original values
    setDisplayName(userData?.displayName || '');
    setPhoneNumber(userData?.phoneNumber || '');
    setSmsOptIn(userData?.smsOptIn || false);
    setSkills(userData?.skills || []);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }
    try {
      setPhotoUploading(true);
      setError('');
      const ext = file.name.split('.').pop();
      const photoRef = storageRef(storage, `profiles/${currentUser.uid}/pending.${ext}`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);
      await updateUserDocument(currentUser.uid, { pendingPhotoURL: url });
      await refreshUserData();
      setSuccess('Photo submitted for approval. It will be visible once approved by your org admin.');
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleJoinOrg = async () => {
    if (!joinInviteCode.trim()) return;
    try {
      setJoiningOrg(true);
      setJoinError('');
      setJoinSuccess('');

      const org = await verifyOrganizationByInviteCode(joinInviteCode.trim());
      if (!org) {
        setJoinError('Invalid or expired invite code. Please check and try again.');
        return;
      }

      // Check if user is already in this org
      const alreadyMember = userOrganizations.some(o => o.organizationId === org.organizationId);
      if (alreadyMember) {
        setJoinError('You are already a member of this organization.');
        return;
      }

      await addOrganizationToUser(currentUser.uid, org.organizationId, org.organizationName);
      await refreshUserData();
      setJoinSuccess(`Successfully joined ${org.organizationName}!`);
      setJoinInviteCode('');
    } catch (err) {
      console.error('Error joining org:', err);
      setJoinError(err.message || 'Failed to join organization. Please try again.');
    } finally {
      setJoiningOrg(false);
    }
  };

  const handlePromoteToCoAdmin = async () => {
    if (!selectedPromotion) return;
    try {
      setPromotingAdmin(true);
      setCoAdminError('');
      setCoAdminSuccess('');
      await promoteToCoAdmin(selectedPromotion.uid, userData.organizationId, userData.organizationName);
      const updated = await getCoAdminsByOrganization(userData.organizationId);
      setCoAdmins(updated);
      const updatedVolunteers = await getVolunteersByOrganization(userData.organizationId);
      setOrgVolunteers(updatedVolunteers);
      setCoAdminSuccess(`${selectedPromotion.displayName} promoted to co-admin.`);
      setSelectedPromotion(null);
    } catch (err) {
      setCoAdminError(err.message || 'Failed to promote co-admin.');
    } finally {
      setPromotingAdmin(false);
    }
  };

  const handleRemoveCoAdmin = async (admin) => {
    try {
      setRemovingAdmin(admin.uid);
      setCoAdminError('');
      setCoAdminSuccess('');
      await removeCoAdmin(admin.uid, userData.organizationId, userData.organizationName);
      const updated = await getCoAdminsByOrganization(userData.organizationId);
      setCoAdmins(updated);
      const updatedVolunteers = await getVolunteersByOrganization(userData.organizationId);
      setOrgVolunteers(updatedVolunteers);
      setCoAdminSuccess(`${admin.displayName} removed as co-admin.`);
    } catch (err) {
      setCoAdminError(err.message || 'Failed to remove co-admin.');
    } finally {
      setRemovingAdmin(null);
    }
  };

  const handleSetDefaultOrg = async (org) => {
    try {
      setSettingDefault(true);
      await setDefaultOrganization(currentUser.uid, org.organizationId, org.organizationName);
      await refreshUserData();
      setSuccess(`${org.organizationName} set as your default organization.`);
    } catch (err) {
      console.error('Error setting default org:', err);
      setError(err.message || 'Failed to set default organization.');
    } finally {
      setSettingDefault(false);
    }
  };

  const handlePushToggle = async () => {
    setPushError('');
    setPushLoading(true);
    try {
      if (userData?.pushNotificationsEnabled) {
        await disablePushNotifications(currentUser.uid);
        await refreshUserData();
      } else {
        const token = await enablePushNotifications(currentUser.uid);
        if (token) await refreshUserData();
        // if null, user dismissed the prompt — do nothing, no error
      }
    } catch (err) {
      setPushError(err.message || 'Failed to update notification settings.');
    } finally {
      setPushLoading(false);
    }
  };

  const handleConfirmLeaveOrg = async () => {
    if (!leaveOrgTarget) return;
    try {
      setLeavingOrg(true);
      const cancelled = await leaveOrganization(currentUser.uid, leaveOrgTarget.organizationId);
      await refreshUserData();
      setLeaveOrgTarget(null);
      setSuccess(
        `You have left ${leaveOrgTarget.organizationName}.` +
        (cancelled > 0 ? ` ${cancelled} upcoming registration${cancelled !== 1 ? 's were' : ' was'} cancelled.` : '')
      );
    } catch (err) {
      console.error('Error leaving org:', err);
      setError(err.message || 'Failed to leave organization. Please try again.');
      setLeaveOrgTarget(null);
    } finally {
      setLeavingOrg(false);
    }
  };

  const isEmailPasswordUser = currentUser?.providerData?.[0]?.providerId === 'password';

  // Get status label and color based on user type
  const getStatusInfo = () => {
    if (!userData?.status) return null;

    // For volunteers, show their approval status
    if (userData.userType === 'volunteer') {
      return {
        label: userData.status,
        color: userData.status === 'active' ? 'success' : 'warning',
      };
    }

    // For org admins, status could be subscription-related in the future
    // (e.g., 'trial', 'active', 'expired', 'suspended')
    if (userData.userType === 'org-admin') {
      // Only show status if it's not active (for future subscription features)
      if (userData.status === 'active') {
        return null; // Don't show "active" chip for org admins
      }
      return {
        label: userData.status,
        color: userData.status === 'trial' ? 'info' : 'warning',
      };
    }

    // Default
    return {
      label: userData.status,
      color: userData.status === 'active' ? 'success' : 'warning',
    };
  };

  const statusInfo = getStatusInfo();

  if (!userData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Profile Card */}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
          {/* Avatar with upload overlay */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              alt={userData.displayName}
              src={userData.photoURL}
              sx={{ width: 100, height: 100, fontSize: '2.5rem', bgcolor: 'primary.main' }}
            >
              {userData.displayName?.charAt(0).toUpperCase()}
            </Avatar>
            {/* Pending badge */}
            {userData.pendingPhotoURL && !editing && (
              <Tooltip title="New photo pending admin approval">
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'warning.main',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}
                >
                  <HourglassTop sx={{ fontSize: 16, color: 'white' }} />
                </Box>
              </Tooltip>
            )}
            {/* Upload button when editing */}
            {editing && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />
                <Tooltip title="Upload profile photo">
                  <Box
                    onClick={() => !photoUploading && fileInputRef.current?.click()}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: photoUploading ? 'default' : 'pointer',
                      transition: 'opacity 0.2s',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                    }}
                  >
                    {photoUploading
                      ? <CircularProgress size={28} sx={{ color: 'white' }} />
                      : <CameraAlt sx={{ color: 'white', fontSize: 28 }} />}
                  </Box>
                </Tooltip>
              </>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              {userData.displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={userData.userType?.replace('-', ' ').toUpperCase()}
                color="primary"
                size="small"
              />
              {statusInfo && (
                <Chip
                  label={statusInfo.label}
                  color={statusInfo.color}
                  size="small"
                />
              )}
              {isEmailPasswordUser && (
                <Chip
                  icon={currentUser.emailVerified ? <CheckCircle /> : <Warning />}
                  label={currentUser.emailVerified ? 'Email Verified' : 'Email Not Verified'}
                  color={currentUser.emailVerified ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                />
              )}
              {userData.pendingPhotoURL && (
                <Chip
                  icon={<HourglassTop sx={{ fontSize: '0.9rem !important' }} />}
                  label="Photo pending approval"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          {!editing && !changingPassword && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Profile Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={!editing || loading}
              variant={editing ? 'outlined' : 'filled'}
              InputProps={{ readOnly: !editing }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={userData.email}
              disabled
              variant="filled"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={!editing || loading}
              variant={editing ? 'outlined' : 'filled'}
              InputProps={{ readOnly: !editing }}
              placeholder="+1 (555) 123-4567"
              helperText="Include country code for SMS (e.g., +1 for US)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              pl: 2,
              pt: 1
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    disabled={!editing || loading || !phoneNumber}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      SMS Reminders
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive text message reminders before events
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pl: 2, pt: 1 }}>
              {(() => {
                const pushSupported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && Notification.permission !== 'denied';
                const pushEnabled = !!userData?.pushNotificationsEnabled;
                return (
                  <Tooltip
                    title={!pushSupported ? (Notification.permission === 'denied' ? 'Notifications are blocked — go to iPhone Settings → Notifications → ServeSync to allow them.' : 'Open ServeSync from your home screen icon (not Safari) to enable push notifications.') : ''}
                    placement="top"
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pushEnabled}
                            onChange={handlePushToggle}
                            disabled={pushLoading || !pushSupported}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {pushEnabled
                              ? <NotificationsActive fontSize="small" color="primary" />
                              : <NotificationsOff fontSize="small" color={pushSupported ? 'action' : 'disabled'} />}
                            <Box>
                              <Typography variant="body1" color={pushSupported ? 'text.primary' : 'text.disabled'}>
                                Push Notifications
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pushSupported
                                  ? (pushEnabled ? 'Enabled — tap to disable' : 'Disabled — tap to enable')
                                  : Notification.permission === 'denied'
                                    ? 'Blocked in Settings'
                                    : 'Use home screen app to enable'}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </span>
                  </Tooltip>
                );
              })()}
              {pushError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {pushError}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                iPhone: add ServeSync to your home screen from Safari first.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="User Type"
              value={userData.userType?.replace('-', ' ').toUpperCase()}
              disabled
              variant="filled"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Organization"
              value={userData.organizationName || 'Not assigned'}
              disabled
              variant="filled"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Sign-in Method"
              value={isEmailPasswordUser ? 'Email/Password' : 'Google'}
              disabled
              variant="filled"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Skills (for volunteers only) */}
          {userData.userType === 'volunteer' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={VOLUNTEER_SKILLS}
                value={skills}
                onChange={(event, newValue) => setSkills(newValue)}
                disabled={!editing || loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Skills"
                    placeholder={editing ? 'Select skills' : ''}
                    variant={editing ? 'outlined' : 'filled'}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={!editing}
                    />
                  ))
                }
              />
            </Grid>
          )}
        </Grid>

        {/* My Organizations Section */}
        {!editing && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  My Organizations
                </Typography>
              </Box>

              {userOrganizations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No organizations found.
                </Typography>
              ) : (
                <List disablePadding>
                  {userOrganizations.map((org) => {
                    const isDefault = org.organizationId === userData?.defaultOrganizationId;
                    return (
                      <ListItem
                        key={org.organizationId}
                        disableGutters
                        sx={{ py: 0.5 }}
                      >
                        <ListItemText
                          primary={org.organizationName}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip label={org.status || 'active'} size="small" color={org.status === 'active' ? 'success' : 'warning'} variant="outlined" />
                              {isDefault && <Chip label="Default" size="small" color="warning" icon={<Star sx={{ fontSize: '0.9rem !important' }} />} />}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={isDefault ? 'Default organization' : 'Set as default'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleSetDefaultOrg(org)}
                                disabled={settingDefault || isDefault}
                                color={isDefault ? 'warning' : 'default'}
                              >
                                {isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {userData?.userType === 'volunteer' && (
                            <Tooltip title="Leave organization">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setLeaveOrgTarget(org)}
                              >
                                <ExitToApp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {/* Join Another Organization */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Join Another Organization
                </Typography>
                {joinError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJoinError('')}>
                    {joinError}
                  </Alert>
                )}
                {joinSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJoinSuccess('')}>
                    {joinSuccess}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    label="Invite Code"
                    value={joinInviteCode}
                    onChange={(e) => setJoinInviteCode(e.target.value.toUpperCase())}
                    disabled={joiningOrg}
                    placeholder="e.g. ABC12345"
                    sx={{ flex: 1, maxWidth: 240 }}
                    onKeyDown={(e) => e.key === 'Enter' && handleJoinOrg()}
                  />
                  <Button
                    variant="contained"
                    onClick={handleJoinOrg}
                    disabled={joiningOrg || !joinInviteCode.trim()}
                    startIcon={joiningOrg ? <CircularProgress size={16} /> : null}
                    size="small"
                  >
                    {joiningOrg ? 'Joining...' : 'Join'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* Co-Admins Section (owner only) */}
        {!editing && isOwner() && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AdminPanelSettings color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Co-Admins
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Co-admins share your admin permissions. Only the owner can manage co-admins.
              </Typography>

              {coAdminError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCoAdminError('')}>
                  {coAdminError}
                </Alert>
              )}
              {coAdminSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCoAdminSuccess('')}>
                  {coAdminSuccess}
                </Alert>
              )}

              {coAdmins.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No co-admins yet.
                </Typography>
              ) : (
                <List disablePadding sx={{ mb: 2 }}>
                  {coAdmins.map((admin) => (
                    <ListItem key={admin.uid} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={admin.displayName}
                        secondary={admin.email}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove co-admin">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCoAdmin(admin)}
                              disabled={removingAdmin === admin.uid}
                            >
                              {removingAdmin === admin.uid
                                ? <CircularProgress size={18} />
                                : <PersonRemove fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Promote a volunteer */}
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Promote a Volunteer to Co-Admin
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Autocomplete
                  options={orgVolunteers}
                  getOptionLabel={(o) => `${o.displayName} (${o.email})`}
                  value={selectedPromotion}
                  onChange={(_, val) => setSelectedPromotion(val)}
                  disabled={promotingAdmin}
                  size="small"
                  sx={{ flex: 1, maxWidth: 360 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select volunteer" />
                  )}
                />
                <Button
                  variant="contained"
                  startIcon={promotingAdmin ? <CircularProgress size={16} /> : <PersonAdd />}
                  onClick={handlePromoteToCoAdmin}
                  disabled={promotingAdmin || !selectedPromotion}
                  size="small"
                >
                  {promotingAdmin ? 'Promoting...' : 'Promote'}
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* Action Buttons */}
        {editing && (
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={loading}
            >
              Save Changes
            </Button>
          </Box>
        )}

        {/* Password Change Section (Email/Password users only) */}
        {isEmailPasswordUser && !editing && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Change Password
              </Typography>

              {!changingPassword ? (
                <Button
                  variant="outlined"
                  onClick={() => setChangingPassword(true)}
                >
                  Change Password
                </Button>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                              >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handlePasswordChange}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Leave Organization Confirmation Dialog */}
      <Dialog
        open={Boolean(leaveOrgTarget)}
        onClose={() => !leavingOrg && setLeaveOrgTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Leave Organization?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to leave <strong>{leaveOrgTarget?.organizationName}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            All of your <strong>upcoming</strong> event registrations with this organization will be
            cancelled automatically. Past volunteer history is preserved for the organization's records.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveOrgTarget(null)} disabled={leavingOrg}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmLeaveOrg}
            disabled={leavingOrg}
            startIcon={leavingOrg ? <CircularProgress size={18} color="inherit" /> : <ExitToApp />}
          >
            {leavingOrg ? 'Leaving…' : 'Leave Organization'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
